import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import { FilePreviewComponent } from '../../../components/file-preview/file-preview.component';
import { FileService } from '../../../core/api/file.service';
import { ReportService } from '../../../core/api/report.service';
import { AuthService } from '../../../core/services/auth.service';
import { HelpersService } from '../../../core/services/helpers.service';
import { LoadingService } from '../../../core/services/loading.service';

@Component({
  selector: 'app-final-transaction-recap',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatDividerModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './final-transaction-recap.component.html',
  styleUrl: './final-transaction-recap.component.scss',
})
export class FinalTransactionRecapComponent implements OnInit {
  Math = Math;
  currentUser: any;
  searchFormControl = new FormControl();
  displayedColumns = [
    'refId',
    'title',
    'typeData',
    'employeeName',
    'teamName',
    'tagProjectName',
    'status',
    'transactionDate',
    'completedAt',
    'requestedAmount',
    'spentAmount',
    'balanceAmount',
    'files',
  ];
  dataSource = new MatTableDataSource<any>([]);
  summary = {
    totalTransactions: 0,
    totalReimbursements: 0,
    totalCashAdvances: 0,
    totalFiles: 0,
    totalRequestedAmount: 0,
    totalSpentAmount: 0,
    totalBalanceAmount: 0,
  };
  q = {
    page: 1,
    limit: 10,
    search: '',
  };
  dataTotal = 0;
  pageSizeOption = [10, 20, 50, 100];

  constructor(
    private reportService: ReportService,
    private authService: AuthService,
    private helpers: HelpersService,
    private router: Router,
    private dialog: MatDialog,
    private fileService: FileService,
    private loadingService: LoadingService,
  ) {
    this.currentUser = this.authService.getLoggedUser();
  }

  ngOnInit(): void {
    if (!this.authService.isSysAdmin(this.currentUser)) {
      this.helpers.sbError('Anda tidak memiliki akses ke menu ini');
      this.router.navigate(['/dashboard/home']);
      return;
    }

    this.getData();

    this.searchFormControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: any) => {
        this.q.search = value ?? '';
        this.q.page = 1;
        this.getData();
      });
  }

  getData(): void {
    this.reportService
      .getFinalTransactionRecap(this.q)
      .then((res: any) => {
        this.summary = res?.summary ?? this.summary;
        this.dataSource.data = res?.reports ?? [];
        this.dataTotal = res?.meta?.totalData ?? 0;
      })
      .catch((err) => {
        this.helpers.sbError(err?.message ?? 'Gagal memuat data rekap');
      });
  }

  onPageChange(page: PageEvent): void {
    this.q.limit = page.pageSize;
    this.q.page = page.pageIndex + 1;
    this.getData();
  }

  exportExcel(): void {
    this.reportService
      .exportFinalTransactionRecap({ search: this.q.search })
      .then((item) => {
        if (item?.base64 && item?.fileName) {
          this.downloadBase64File(item.base64, item.fileName);
          this.helpers.sbSuccess('Excel berhasil diunduh');
          return;
        }

        this.helpers.sbError('Gagal mengekspor Excel');
      })
      .catch((err) => {
        const message =
          err?.message ??
          err?.error?.message ??
          (typeof err === 'string' ? err : null) ??
          'Gagal mengekspor Excel';
        this.helpers.sbError(message);
      });
  }

  private downloadBase64File(base64: string, fileName: string): void {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  previewFile(file: any): void {
    this.dialog.open(FilePreviewComponent, {
      width: '70vw',
      maxWidth: '70vw',
      maxHeight: '95vh',
      data: file,
    });
  }

  downloadFile(file: any): void {
    this.loadingService.startLoading();
    this.fileService.download(file.id).subscribe(
      (res) => {
        this.loadingService.endLoading();
        const url = URL.createObjectURL(res);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.download = file.fileName;
        anchor.click();
        URL.revokeObjectURL(url);
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  getStatusClass(status?: string): string {
    return status === 'Reimbursed'
      ? 'bg-emerald-100 text-emerald-900'
      : 'bg-slate-200 text-slate-900';
  }

  getTypeClass(type?: string): string {
    return type === 'Reimbursement'
      ? 'bg-cyan-100 text-cyan-900'
      : 'bg-blue-100 text-blue-900';
  }

  hasBalance(element: any): boolean {
    return element?.typeData === 'Cash Advance';
  }

  getBalanceClass(balance?: number | null): string {
    if (balance === null || balance === undefined) return 'text-slate-700';
    if (balance < 0) return 'text-rose-600';
    if (balance > 0) return 'text-emerald-600';
    return 'text-slate-700';
  }
}
